<script setup lang="ts">
/**
 * The lobby chat, docked in the garage.
 *
 * ── Why a dock and not a column in the garage grid ───────────────────────────
 *
 * `GarageScreen`'s body grid is width-tuned to the pixel and written up at
 * length — the tab strip's own stylesheet does arithmetic on a 1,357px row to
 * explain why the tabs are allowed to give way first. Adding a third column
 * there means re-deriving all of that for every tab and every breakpoint, to
 * place something that is deliberately peripheral. A fixed dock in the corner
 * costs the grid nothing and can be collapsed outright.
 *
 * ── Collapsed is a real state, and it is remembered ──────────────────────────
 *
 * Somebody who does not want a chat room on their screen should be able to shut
 * it and have it stay shut. The preference is `localStorage`, beside the
 * settings, because it is a fact about this browser rather than about the
 * account — exactly the argument `state/settings.ts` makes for the aim
 * sensitivity.
 *
 * ── The unread mark ──────────────────────────────────────────────────────────
 *
 * Counted only while collapsed, and cleared on open. A count that kept running
 * while the panel was visible would be a permanent badge on a room the player
 * is already reading.
 *
 * ── What it deliberately does not do ─────────────────────────────────────────
 *
 * No mention highlighting, no direct messages, no moderation UI, no emoji
 * picker. This is the basic room that was asked for; each of those is a feature
 * with its own server half, and half of one is worse than none.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { CHAT_MAX_LENGTH, say, startChat, useChat } from '@/state/chat';
import { useSession } from '@/state/session';

const chat = useChat();
const session = useSession();

const STORAGE_KEY = 'sow.chat.open';

const open = ref(true);
const draft = ref('');
const listEl = ref<HTMLElement | null>(null);
const unread = ref(0);

/**
 * How close to the bottom still counts as following the room.
 *
 * Roughly two lines. Generous on purpose: the rule is *stop following when the
 * reader has gone looking at something*, not *stop following the instant the
 * scrollbar is a pixel off the end*, and a tight threshold turns a trackpad's
 * momentum overshoot into an accidental unfollow.
 */
const FOLLOW_SLACK_PX = 48;

/** True while the reader is at the bottom and should be carried by new lines. */
const pinnedToBottom = ref(true);

/** New lines that arrived while the reader was scrolled up. */
const missed = ref(0);

/**
 * The newest message's id — what "a new message arrived" actually means.
 *
 * This used to watch `chat.messages.value.length`, which stops changing the
 * moment the client's ring buffer is full: `KEEP` is 80 in `state/chat.ts`, so
 * after ~20 lines past the seeded history every arrival evicts one and the
 * length sits at 80 for ever. The watcher stopped firing, and with it both the
 * auto-scroll and the unread count — permanently, in a room that had been
 * working fine for a minute. An id changes on every arrival whatever the buffer
 * is doing.
 */
const newestId = computed(() => chat.messages.value.at(-1)?.id ?? null);

onMounted(() => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null) open.value = stored === '1';
  } catch {
    // A browser with storage disabled gets the default. Not worth a branch.
  }
  startChat();
  void scrollToEnd();
});

watch(open, (isOpen) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, isOpen ? '1' : '0');
  } catch {
    /* ignored — see onMounted */
  }
  if (isOpen) {
    unread.value = 0;
    void scrollToEnd();
  }
});

/**
 * A new line arrived.
 *
 * Three cases, and the third is the one that is easy to miss.
 *
 * 1. **Collapsed** — count it, do nothing else.
 * 2. **Following the room** — scroll to the end, which is the ordinary case and
 *    what a chat should do by default.
 * 3. **Reading something further up** — do *not* scroll, and also do not let
 *    the view drift. The client keeps a fixed-size buffer, so every arrival
 *    past the cap drops a line off the *top*: the content above the reader
 *    shrinks, and the browser keeps `scrollTop` where it was, so the text they
 *    are reading slides upward by a row. That is the jitter that makes a busy
 *    room unreadable, and it happens precisely when somebody is trying to read.
 *    `scrollTop` is corrected by the height the list lost.
 *
 * The watcher runs before the DOM updates (Vue's default `pre` flush), so the
 * measurements taken here are the *old* layout — which is exactly what the
 * correction needs.
 */
watch(newestId, (id, previous) => {
  if (id === null || id === previous) return;

  if (!open.value) {
    unread.value = Math.min(unread.value + 1, 99);
    return;
  }

  if (pinnedToBottom.value) {
    void scrollToEnd();
    return;
  }

  missed.value = Math.min(missed.value + 1, 99);
  const el = listEl.value;
  if (!el) return;
  const heightBefore = el.scrollHeight;
  const topBefore = el.scrollTop;
  void nextTick().then(() => {
    const after = listEl.value;
    if (!after) return;
    const lost = heightBefore - after.scrollHeight;
    if (lost > 0) after.scrollTop = Math.max(0, topBefore - lost);
  });
});

async function scrollToEnd(): Promise<void> {
  await nextTick();
  const el = listEl.value;
  if (!el) return;
  el.scrollTop = el.scrollHeight;
  pinnedToBottom.value = true;
  missed.value = 0;
}

function onScroll(): void {
  const el = listEl.value;
  if (!el) return;
  const wasPinned = pinnedToBottom.value;
  pinnedToBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK_PX;
  // Scrolling back down to the end is the same intent as pressing the jump
  // button, so it clears the same counter.
  if (!wasPinned && pinnedToBottom.value) missed.value = 0;
}

const mine = computed(() => session.player.value?.nickname ?? null);

const remaining = computed(() => CHAT_MAX_LENGTH - draft.value.length);

async function send(): Promise<void> {
  if (draft.value.trim().length === 0) return;
  const sent = await say(draft.value);
  if (sent) {
    draft.value = '';
    // Saying something is the clearest possible statement that you want to see
    // what happens next, so it re-follows the room however far up you were.
    void scrollToEnd();
  }
}

/** `HH:MM` in the reader's own timezone, from the server's epoch stamp. */
function clock(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
</script>

<template>
  <section class="chat-dock" :class="{ collapsed: !open }" data-test="chat-dock">
    <header class="chat-head">
      <button
        type="button"
        class="chat-toggle"
        data-test="chat-toggle"
        :aria-expanded="open"
        :title="open ? 'Hide the lobby chat' : 'Show the lobby chat'"
        @click="open = !open"
      >
        <svg class="chat-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            d="M2 3.5h12a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H6.6L3 14.5V11.5H2a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z"
            fill="currentColor"
          />
        </svg>
        <span class="chat-title">Lobby</span>
        <span v-if="!open && unread > 0" class="chat-unread" data-test="chat-unread">
          {{ unread }}
        </span>
        <svg class="chat-chevron" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path
            :d="open ? 'M2.5 4.5L6 8l3.5-3.5' : 'M2.5 7.5L6 4l3.5 3.5'"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </header>

    <div v-if="open" class="chat-body">
      <ol ref="listEl" class="chat-list" data-test="chat-list" @scroll="onScroll">
        <li v-if="chat.messages.value.length === 0" class="chat-empty">
          Nobody has said anything yet.
        </li>
        <li
          v-for="message in chat.messages.value"
          :key="message.id"
          class="chat-line"
          :class="{ own: message.author === mine }"
        >
          <time class="chat-time" :datetime="new Date(message.at).toISOString()">
            {{ clock(message.at) }}
          </time>
          <span class="chat-author">{{ message.author }}</span>
          <span class="chat-text">{{ message.body }}</span>
        </li>
      </ol>

      <!-- Counted while the reader is scrolled up, and offered rather than
           acted on: the whole point of not following is that they are reading
           something, so the way back is a button they press, not a jump the
           room performs for them. -->
      <button
        v-if="!pinnedToBottom && missed > 0"
        type="button"
        class="chat-jump"
        data-test="chat-jump"
        @click="scrollToEnd()"
      >
        {{ missed }} new {{ missed === 1 ? 'message' : 'messages' }} ↓
      </button>

      <form class="chat-form" @submit.prevent="send">
        <input
          v-model="draft"
          type="text"
          class="chat-input"
          data-test="chat-input"
          :maxlength="CHAT_MAX_LENGTH"
          placeholder="Say something…"
          aria-label="Message the lobby"
          autocomplete="off"
          :disabled="chat.sending.value"
          @input="chat.clearError()"
        />
        <button
          type="submit"
          class="chat-send"
          data-test="chat-send"
          :disabled="chat.sending.value || draft.trim().length === 0"
        >
          Send
        </button>
      </form>

      <p v-if="chat.error.value" class="chat-note bad" data-test="chat-error">
        {{ chat.error.value }}
      </p>
      <p v-else-if="remaining < 40" class="chat-note">{{ remaining }} left</p>
    </div>
  </section>
</template>

<style scoped>
/*
 * Fixed to the bottom-left. The garage's own account block and gear live top
 * right, the loadout sidebar is left-of-centre, and the deploy affordances are
 * centre — bottom-left is the one corner nothing else claims.
 *
 * `z-index` sits under the settings window and the profile overlay on purpose:
 * a dialog is modal and a chat room is not, so the room goes behind.
 */
.chat-dock {
  position: fixed;
  left: 1rem;
  bottom: 1rem;
  z-index: 20;
  display: flex;
  flex-direction: column;
  width: min(23rem, calc(100vw - 2rem));
  max-height: min(26rem, 55vh);
  border: 1px solid var(--steel-600);
  border-radius: 0.35rem;
  background: color-mix(in srgb, var(--steel-900) 88%, transparent);
  backdrop-filter: blur(6px);
  box-shadow: 0 0.6rem 1.6rem rgb(0 0 0 / 45%);
  overflow: hidden;
}

/* Collapsed is the header alone — a bar, not a hidden element, so the way back
   is always on screen. */
.chat-dock.collapsed {
  max-height: none;
}

.chat-head {
  flex: none;
}

.chat-toggle {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  /* 44px of height, the tap floor the mobile pass established. */
  min-height: 2.75rem;
  padding: 0 0.75rem;
  border: none;
  background: color-mix(in srgb, var(--steel-700) 70%, transparent);
  color: var(--steel-200);
  font: inherit;
  cursor: pointer;
}

.chat-toggle:hover {
  background: var(--steel-700);
}

/* The bubble mark only draws in the one state it is for — see the compact
   collapsed rule below. Everywhere else the label already says what this is. */
.chat-icon {
  display: none;
  flex: none;
  width: 1.1rem;
  height: 1.1rem;
}

.chat-title {
  flex: 1;
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-align: left;
}

.chat-unread {
  min-width: 1.25rem;
  padding: 0.05rem 0.35rem;
  border-radius: 999px;
  background: var(--accent);
  color: var(--steel-900);
  font-family: var(--font-mono);
  font-size: 0.65rem;
  font-weight: 600;
}

.chat-chevron {
  width: 0.75rem;
  height: 0.75rem;
  flex: none;
  opacity: 0.7;
}

.chat-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.chat-list {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0.5rem 0.75rem;
  list-style: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  font-size: 0.8rem;
  line-height: 1.45;
}

.chat-empty {
  color: var(--steel-400);
  font-style: italic;
}

/*
 * One wrapped paragraph per line rather than a grid: a call sign is 3–20
 * characters, and a fixed author column either truncates the long ones or
 * wastes a third of a 23rem panel on the short ones. Inline keeps the text
 * doing the work.
 */
.chat-line {
  margin-bottom: 0.3rem;
  word-break: break-word;
}

.chat-time {
  margin-right: 0.35rem;
  color: var(--steel-400);
  font-family: var(--font-mono);
  font-size: 0.65rem;
}

.chat-author {
  margin-right: 0.35rem;
  color: var(--steel-200);
  font-weight: 600;
}

.chat-author::after {
  content: ':';
  color: var(--steel-400);
  font-weight: 400;
}

/* The player's own lines, marked so they can find themselves in the scroll.
   Colour only — no alignment flip, which at this width would make a
   two-line message harder to read than it is worth. */
.chat-line.own .chat-author {
  color: var(--accent);
}

.chat-text {
  color: var(--steel-200);
}

/* Sits directly above the input, full width, so it reads as a strip belonging
   to the list rather than as a floating pill over the last message. */
.chat-jump {
  flex: none;
  margin: 0;
  padding: 0.3rem 0.5rem;
  border: none;
  border-top: 1px solid var(--steel-700);
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  text-align: center;
  cursor: pointer;
}

.chat-jump:hover {
  background: color-mix(in srgb, var(--accent) 28%, transparent);
  color: #fff;
}

.chat-form {
  flex: none;
  display: flex;
  gap: 0.4rem;
  padding: 0.5rem;
  border-top: 1px solid var(--steel-700);
}

.chat-input {
  flex: 1;
  min-width: 0;
  min-height: 2.25rem;
  padding: 0 0.55rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.25rem;
  background: var(--steel-800);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.8rem;
}

.chat-input:focus {
  outline: none;
  border-color: var(--accent-dim);
}

.chat-send {
  flex: none;
  min-height: 2.25rem;
  padding: 0 0.7rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.25rem;
  background: var(--steel-700);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
}

.chat-send:hover:not(:disabled) {
  border-color: var(--accent-dim);
  color: #fff;
}

.chat-send:disabled {
  opacity: 0.45;
  cursor: default;
}

.chat-note {
  flex: none;
  margin: 0;
  padding: 0 0.75rem 0.5rem;
  color: var(--steel-400);
  font-size: 0.7rem;
}

.chat-note.bad {
  color: #e88b6c;
}

/*
 * A landscape phone is ~375px tall, and the dock's own max-height would take
 * most of it. Narrower and shorter there, and the garage's own compact rules
 * already move the account block out of the way.
 *
 * ── The toggle goes below the bay row; the open panel may cover it ───────────
 *
 * Owner instruction, 2026-09-02, and it reverses the previous fix rather than
 * refining it. That fix *lifted* the whole dock to `bottom: 7.5rem` so it stood
 * clear of `GarageLobby`'s bay row — which it did, and the cost was that the
 * collapsed bar, the one thing on screen when the room is shut, sat in the
 * middle of the screen with the bays underneath it. The rule wanted is narrower
 * than "do not overlap": **the toggle must never be over the bays, and the panel
 * may be.** A chat room a player has deliberately opened is allowed to cover a
 * bookmark bar; a permanently-visible bar is not.
 *
 * So the dock is back at the bottom-left corner and `column-reverse` puts the
 * header at the *bottom* of it, so the panel grows upward out of a toggle that
 * stays put. Collapsed, that is a 44px bar in the corner; open, it reaches up
 * over the bays and stops well short of the tab strip.
 *
 * **The clearance moved to `GarageLobby` and inverted.** It used to be this
 * file's `--bay-clearance`, measured against the bay row's *top* edge; what has
 * to hold now is that the bay row's **bottom** edge clears the toggle's **top**
 * edge, and only the lobby can move a row in its own grid. `.lobby.compact
 * .dock`'s bottom margin is 3rem for that reason, and its comment carries the
 * measurement. Both halves are bottom-anchored, so the figure does not move with
 * the viewport — it is the same clearance at 667x375, 844x390 and 915x412.
 *
 * **The width is 15rem rather than 18.** The hangar's *Drag to look around* hint
 * is pinned bottom-centre, and at 667 the 18rem dock's right edge (296px) would
 * have landed on the hint's left end — the same bottom-edge collision that
 * retired the music chip, arriving from the other side. 15rem stops at 248px
 * against a hint measured at 264.6, so 16.6px clear; the overlap is 0 at all
 * three compact sizes, and the hint moves right as the viewport widens.
 *
 * **This is compact-only, and desktop must not inherit it.** At 1180x820 the
 * dock is at x 16–384 and the bays at x 419–761 — side by side, overlap zero,
 * and the extra height is the reason a desktop dock is worth reading.
 *
 * **45vh keeps its number and gets a new reason.** It was chosen while the dock
 * was lifted — 60vh, once raised, ran under the tab strip — and that constraint
 * is gone, so it was re-measured against the neighbour that is left. Anchored at
 * the bottom, the height that used to be about the bays is now about the
 * *daily-quest card*, top-left, which a taller dock climbs into:
 *
 *   45vh   list 70–91px    quests overlapped by 0–1,121px²
 *   55vh   list 107–128px  quests overlapped by 6,004–9,881px²
 *   60vh   list 126–148px  quests overlapped by 10,816–14,273px²
 *
 * (667x375, 844x390, 915x412, open, touch emulation. None of the three reaches
 * the tab strip.) 45vh is therefore the tier that leaves the quest card readable
 * — the lifted dock crossed it by 11–15k px² and that was recorded here as a
 * standing condition, which it no longer has to be. A taller room is available
 * for the asking; it costs the quest card.
 */
@media (max-height: 520px) {
  .chat-dock {
    left: 0.5rem;
    bottom: 0.5rem;
    /* The toggle at the bottom, the room growing up out of it. */
    flex-direction: column-reverse;
    width: min(15rem, calc(100vw - 1rem));
    max-height: 45vh;
  }

  /* Explicit, because the dock's height is now content-driven up to a cap and
     the list is what has to give: without it a tall room pushes the form and
     the toggle off the bottom of a box that cannot scroll. */
  .chat-dock .chat-body {
    flex: 1 1 auto;
    min-height: 0;
  }

  /*
   * Collapsed *and* compact: a 240px label bar sitting in the corner of a
   * ~375px-tall screen the whole session is real, permanent occupation —
   * "closed" has to mean small, not merely inert. So the bar shrinks to the
   * one thing a closed room needs to be: a 44px round icon a thumb can find
   * and reopen, badge and all. The open state is untouched — the label and
   * chevron earn their width once there is a room behind them to read.
   */
  .chat-dock.collapsed {
    width: 2.75rem;
  }

  .chat-dock.collapsed .chat-toggle {
    justify-content: center;
    width: 2.75rem;
    padding: 0;
    border-radius: 999px;
  }

  .chat-dock.collapsed .chat-icon {
    display: block;
  }

  .chat-dock.collapsed .chat-title,
  .chat-dock.collapsed .chat-chevron {
    display: none;
  }

  .chat-dock.collapsed .chat-unread {
    position: absolute;
    top: 0.1rem;
    right: 0.1rem;
  }
}
</style>
